import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2026-10-12 18:23:11 +0200 (Mon, 12 Oct 2026) $
//  Author    : Olli Powroznik
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 166492 $
//  Purpose   : presentation support package
// =============================================================================
// ---Third-party imports
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
// ---Framework imports
import { size } from 'ldash';
import { Sel } from 'uix/registries';
import log from 'uix/logging';
import { useSelector, useRef } from 'uix/hooks';
import S from './audioticker.css';
export const AudioWaves = ({ active, hidden = true, onClick, ariaLabel, muted, className })=>{
    return /*#__PURE__*/ _jsxs("div", {
        onClick: onClick,
        role: onClick ? 'button' : undefined,
        tabIndex: onClick ? 0 : undefined,
        "aria-label": onClick ? ariaLabel : undefined,
        "aria-pressed": onClick ? !!muted : undefined,
        onKeyDown: onClick ? (e)=>{
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
            }
        } : undefined,
        className: clsx(S.loader, active && S.play, hidden && S.hidden, className),
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: clsx(muted && S.muted)
            }),
            /*#__PURE__*/ _jsxs("svg", {
                className: S.wave,
                "data-name": "Layer 1",
                xmlns: "http://www.w3.org/2000/svg",
                viewBox: "0 0 50 38.05",
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "Audio Wave"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l1,
                        "data-name": "Line 1",
                        d: "M0.91,15L0.78,15A1,1,0,0,0,0,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H0.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l2,
                        "data-name": "Line 2",
                        d: "M6.91,9L6.78,9A1,1,0,0,0,6,10V28a1,1,0,1,0,2,0s0,0,0,0V10A1,1,0,0,0,7,9H6.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l3,
                        "data-name": "Line 3",
                        d: "M12.91,0L12.78,0A1,1,0,0,0,12,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H12.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l4,
                        "data-name": "Line 4",
                        d: "M18.91,10l-0.12,0A1,1,0,0,0,18,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H18.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l5,
                        "data-name": "Line 5",
                        d: "M24.91,15l-0.12,0A1,1,0,0,0,24,16v6a1,1,0,0,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H24.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l6,
                        "data-name": "Line 6",
                        d: "M30.91,10l-0.12,0A1,1,0,0,0,30,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H30.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l7,
                        "data-name": "Line 7",
                        d: "M36.91,0L36.78,0A1,1,0,0,0,36,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H36.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l8,
                        "data-name": "Line 8",
                        d: "M42.91,9L42.78,9A1,1,0,0,0,42,10V28a1,1,0,1,0,2,0s0,0,0,0V10a1,1,0,0,0-1-1H42.91Z"
                    }),
                    /*#__PURE__*/ _jsx("path", {
                        className: S.l9,
                        "data-name": "Line 9",
                        d: "M48.91,15l-0.12,0A1,1,0,0,0,48,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H48.91Z"
                    })
                ]
            })
        ]
    });
};
export const AudioTicker = ({ audio, audioDelay, started, playerState, setPlayerState })=>{
    const audioRef = useRef();
    // const audioCtx = new AudioContext()
    let apiurl = useSelector(Sel.apiurl);
    let i18n = useSelector(Sel.i18n);
    let { muted, playing, ended } = playerState;
    useEffect(()=>{
        let ref = audioRef.current;
        // if (!audioCtx.state == 'suspended') {
        if (started && !ended) {
            setTimeout(()=>{
                ref.play();
            }, audioDelay * 1000);
        } else {
            ref.load();
        }
    // }
    }, [
        started,
        audioRef.current,
        playing
    ]);
    useEffect(()=>{
        if (audioRef.current) {
            audioRef.current.muted = muted;
        }
    }, [
        muted
    ]);
    let endHandler = ()=>{
        setPlayerState({
            ended: true
        });
    };
    let muteHandle = ()=>{
        setPlayerState({
            muted: !muted
        });
    };
    // do not access really short audio urls
    let isValidSource = size(audio) > 3;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(AudioWaves, {
                onClick: muteHandle,
                ariaLabel: i18n.get('pres_media_mute', 'Stummschalten'),
                active: playing & !ended,
                muted: muted
            }),
            /*#__PURE__*/ _jsx("audio", {
                preload: "none",
                onEnded: endHandler,
                ref: audioRef,
                children: isValidSource && /*#__PURE__*/ _jsx("source", {
                    src: apiurl.href(audio)
                })
            })
        ]
    });
};
